import React, { Fragment, memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { AdminInviteInternalUser } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useInternalPermissions } from 'src/hooks/useInternalPermissions'
import type {
  InternalAccessUser,
  InviteInternalUserFormValues,
} from 'src/interfaces/internalAccess'
import { getInternalRoles, getInternalUserSaving } from 'src/selectors/internalAccess'
import { buildAssignableRoleOptions } from 'src/utils/internalAccess/internalUsers'
import { inviteInternalUserFormValidator } from './InviteInternalUserForm.validator'

const FORM_ID = 'invite-internal-user-form'

const INITIAL_VALUES: InviteInternalUserFormValues = { email: '', roleId: '' }

interface Props {
  isOpen: boolean
  onClose(): void
}

/**
 * Same Formik-around-`CommonModal` shape as `InternalUserModal`, so the footer's submit reads the
 * form through context. Unmounted while closed, so every open starts from an empty form.
 */
const InviteInternalUserModalComponent = ({ isOpen, onClose }: Props) => {
  const roles = useSelector(getInternalRoles)
  const grantable = useInternalPermissions()
  const isSaving = useSelector(getInternalUserSaving)
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const roleOptions = useMemo(() => {
    return buildAssignableRoleOptions(roles, grantable)
  }, [roles, grantable])

  const handleSubmit = useCallback(
    async (values: InviteInternalUserFormValues) => {
      const result = await asyncDispatchWithErrorToast<InternalAccessUser>(
        AdminInviteInternalUser.request({ email: values.email.trim(), roleId: values.roleId }),
      )

      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('User invited', { variant: 'success' })
      onClose()
    },
    [asyncDispatchWithErrorToast, onClose, snackbar],
  )

  // A sent invite cannot be cancelled, so the modal stays open until the request settles.
  const handleClose = useCallback(() => {
    if (isSaving) {
      return
    }

    onClose()
  }, [isSaving, onClose])

  if (!isOpen) {
    return null
  }

  return (
    <Formik<InviteInternalUserFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={inviteInternalUserFormValidator}
      onSubmit={handleSubmit}
    >
      <CommonModal
        isOpen
        title="Add user"
        onClose={handleClose}
        className={tw`max-w-[560px]`}
        closeButtonTestId="invite-internal-user-modal-close"
        buttons={
          <Fragment>
            <Button
              size="small"
              color="grey"
              onClick={handleClose}
              disabled={isSaving}
              data-testid="invite-internal-user-cancel-button"
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="dark"
              type="submit"
              form={FORM_ID}
              loading={isSaving}
              disabled={isSaving}
              data-testid="invite-internal-user-submit-button"
            >
              Add user
            </Button>
          </Fragment>
        }
      >
        <Form id={FORM_ID} data-testid="invite-internal-user-modal">
          <div css={tw`flex flex-col gap-4`}>
            <Typography variant="body2" css={tw`text-shades-500`}>
              They sign in with their Google account for this email and start with the role you pick
              here.
            </Typography>
            <FormikTextField<InviteInternalUserFormValues>
              name="email"
              label="Email"
              placeholder="name@gigsafe.com"
              type="email"
              fullWidth
              disabled={isSaving}
              data-testid="invite-internal-user-email-input"
            />
            <FormikSelect<InviteInternalUserFormValues>
              name="roleId"
              label="Role"
              placeholder="Select a role"
              options={roleOptions}
              disabled={isSaving}
              data-testid="invite-internal-user-role-select"
            />
            {roleOptions.length === 0 ? (
              <Typography variant="body2" css={tw`text-xs text-shades-500`}>
                No roles you can assign.
              </Typography>
            ) : null}
          </div>
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const InviteInternalUserModal = memo(InviteInternalUserModalComponent)
